import type { AiCategoryMaster, AiCategoryMasterRow } from '@citizen-walk/shared-types';
import {
  resolveClassificationTitleByAxis,
  resolveMasterRow,
  resolveReasonText,
  STREET_SURVEY_DIRECTION_LABELS,
} from '@citizen-walk/shared-types';
import { resolveChipColor, resolveChipEmoji } from '../posts-moderation';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';
import type { StreetSurveyPost } from './useStreetSurveyPosts';

/**
 * SVピン (rank) の色・絵文字・tooltip解決 (docs/25 §3-1/§4-9/§11)。2Dイメージボードと
 * 3Dパノラマの両方で同じ動的カラールール (`resolveChipColor` + primary軸) を使うための共通ロジック。
 * 絵文字は色分けする調査項目 (机上調査の設定で選ぶ) のマスタに絵文字があるときだけ解決し、
 * 無ければ undefined (機構は特定の項目に依存しない、docs/25 §3 中核原則)。
 */

/**
 * マスタ行の解決 (コード/名称 → 行) は shared-types に移した (2026-09-18)。
 * 画面とエクスポート (functions) が同じ解決をするため (docs/98 R-98 / R-99)。
 */
export { buildByCategoryCdMap, resolveMasterRow } from '@citizen-walk/shared-types';

export function resolvePinColor(
  post: StreetSurveyPost,
  primaryAxis: PrimaryAxisResolution,
  rank: number,
): string {
  const classification = post.classifications.find((c) => c.rank === rank);
  if (!classification || !primaryAxis.primaryAxis) return '#94A3B8';
  const code = String(classification[primaryAxis.primaryAxis.codeField] ?? '');
  const row = resolveMasterRow(code, primaryAxis.byCategoryCd);
  return row ? resolveChipColor(row, primaryAxis.master ?? undefined) : '#94A3B8';
}

/** 色分けする調査項目から絵文字を解決する (項目が無ければ undefined)。 */
export function resolvePinRoleEmoji(
  post: StreetSurveyPost,
  roleTypeAxis: RoleTypeAxisResolution,
  rank: number,
): string | undefined {
  if (!roleTypeAxis.axis) return undefined;
  const classification = post.classifications.find((c) => c.rank === rank);
  if (!classification) return undefined;
  const code = String(classification[roleTypeAxis.axis.codeField] ?? '');
  const row = resolveMasterRow(code, roleTypeAxis.byCategoryCd);
  return row ? resolveChipEmoji(row) : undefined;
}

/** 色分けする調査項目から色を解決する (項目が無ければ undefined)。 */
export function resolvePinRoleColor(
  post: StreetSurveyPost,
  roleTypeAxis: RoleTypeAxisResolution,
  rank: number,
): string | undefined {
  if (!roleTypeAxis.axis) return undefined;
  const classification = post.classifications.find((c) => c.rank === rank);
  if (!classification) return undefined;
  const code = String(classification[roleTypeAxis.axis.codeField] ?? '');
  const row = resolveMasterRow(code, roleTypeAxis.byCategoryCd);
  return row ? resolveChipColor(row, roleTypeAxis.master ?? undefined) : undefined;
}

/**
 * 「地図の色分けに使う軸」の汎用形 (docs/25c 結果地図、2026-07-14)。`PrimaryAxisResolution` /
 * `RoleTypeAxisResolution` はどちらも `{codeField, master, byCategoryCd}` を持つ同型なので、
 * 集計・表示ロジックはこの汎用形だけを見れば軸の種類を意識せずに書ける。
 */
export interface CategoryAxisResolution {
  codeField: string;
  master: AiCategoryMaster | null;
  byCategoryCd: Map<string, AiCategoryMasterRow>;
}

/**
 * 結果地図の色分け・凡例に使う軸を解決する (docs/25c、2026-07-14)。
 * role_type 軸 (地区が任意設定) があればそれを優先し、無ければ primary 軸
 * (walker=GQL、keikan=景観の観点 等、M3 で必須設定) にフォールバックする。
 * どちらも無い場合のみ null (呼出側は単色で表示し、色分けの凡例を出さない) — ただし
 * primary 軸は M3 編集画面で必須項目のため、実質的にほぼ常に非 null になる想定。
 *
 * 修正前は role_type 軸のみを見ており、role_type を定義していない地区 (keikan/kids-bousai 等)
 * では常に W-Score (walker 固有の点数、他モードでは常に 0 で無意味) にフォールバックしていた
 * (2026-09-16 に地点のスコア表示自体を撤去)。
 */
export function resolveMapLegendAxis(
  primaryAxis: PrimaryAxisResolution,
  roleTypeAxis?: RoleTypeAxisResolution,
): { source: 'role' | 'primary'; label: string; axis: CategoryAxisResolution } | null {
  if (roleTypeAxis?.axis && roleTypeAxis.master) {
    return {
      source: 'role',
      label: roleTypeAxis.axis.label,
      axis: {
        codeField: roleTypeAxis.axis.codeField,
        master: roleTypeAxis.master,
        byCategoryCd: roleTypeAxis.byCategoryCd,
      },
    };
  }
  if (primaryAxis.primaryAxis && primaryAxis.master) {
    return {
      source: 'primary',
      label: primaryAxis.primaryAxis.label,
      axis: {
        codeField: primaryAxis.primaryAxis.codeField,
        master: primaryAxis.master,
        byCategoryCd: primaryAxis.byCategoryCd,
      },
    };
  }
  return null;
}

/**
 * マスタ行の集計・ドーナツ表示用の一意キー。`categoryCd` は大分類 (majorCd) をまたいで
 * 番号が重複することがある (例: `bousai-kids-v4-objects` は "03" が A/B/C/D/E/F/G/P の
 * 各大分類に個別に存在する) ため、素の `categoryCd` だけをキーにすると全く別のカテゴリの
 * 検出数が同じキーに混ざり合ってしまう。`resolveMasterRow`/`buildByCategoryCdMap` と同じ
 * `majorCd-categoryCd` 規約に揃えることで、集計後の逆引きも一意に解決できるようにする。
 */
export function categoryRowKey(row: AiCategoryMasterRow): string {
  return row.majorCd ? `${row.majorCd}-${row.categoryCd}` : row.categoryCd;
}

/**
 * 投稿の代表的カテゴリコードを解決する (最も支配的な検出要素 = rank 最小)。
 * クラスタ集計 (`streetSurveyClusterDonut.ts`) と `resolveCategoryDisplay` の共通ロジック。
 * 軸のコードがマスタに存在しない、分類が無い場合は null。
 */
export function resolvePostCategoryCd(
  post: StreetSurveyPost,
  axis: CategoryAxisResolution,
): string | null {
  // 集計から除く行 (M2 excludeFromAggregation) は代表にしない (集計と地図の色分けを揃える、docs/98 R-98 低)。
  const ranked = [...post.classifications].sort((a, b) => a.rank - b.rank);
  for (const classification of ranked) {
    const code = String(classification[axis.codeField] ?? '');
    const row = resolveMasterRow(code, axis.byCategoryCd);
    if (row && !row.excludeFromAggregation) return categoryRowKey(row);
  }
  return null;
}

/**
 * 投稿内の全検出要素 (classifications 全件) を軸のカテゴリコード別に集計する。
 * 結果地図の個別地点ピンのドーナツ表示 (docs/25、Increment 3 追加) 用 — クラスタ集計
 * (`streetSurveyClusterDonut.ts` の `counts`) は地点単位 (代表カテゴリ1件ずつ) を集約するのに対し、
 * こちらは1地点内の複数検出要素の内訳を数える。
 */
export function resolvePostCategoryCounts(
  post: StreetSurveyPost,
  axis: CategoryAxisResolution,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const c of post.classifications) {
    const code = String(c[axis.codeField] ?? '');
    const row = resolveMasterRow(code, axis.byCategoryCd);
    // 集計から除く行は数えない (エリア / 路線の集計と同じ、docs/98 R-98 低)。
    if (!row || row.excludeFromAggregation) continue;
    const key = categoryRowKey(row);
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

/**
 * 結果地図の投稿単位マーカー表示 (docs/25、Increment 3 追加)。最も支配的な検出要素
 * (rank 最小 = 1) を代表値として色・絵文字を解決する。コードがマスタに存在しない、
 * または分類が無い場合は null (呼出側は単色で表示する)。
 */
export function resolveCategoryDisplay(
  post: StreetSurveyPost,
  axis: CategoryAxisResolution,
): { color: string; emoji?: string } | null {
  const code = resolvePostCategoryCd(post, axis);
  if (!code) return null;
  const row = resolveMasterRow(code, axis.byCategoryCd);
  if (!row) return null;
  return {
    color: resolveChipColor(row, axis.master ?? undefined),
    emoji: resolveChipEmoji(row),
  };
}

/** role_type 専用の薄いラッパ (既存呼出側との後方互換、`resolvePostCategoryCd` 委譲)。 */
export function resolvePostRoleCategoryCd(
  post: StreetSurveyPost,
  roleTypeAxis: RoleTypeAxisResolution,
): string | null {
  if (!roleTypeAxis.axis) return null;
  return resolvePostCategoryCd(post, {
    codeField: roleTypeAxis.axis.codeField,
    master: roleTypeAxis.master,
    byCategoryCd: roleTypeAxis.byCategoryCd,
  });
}

/** role_type 専用の薄いラッパ (既存呼出側との後方互換、`resolvePostCategoryCounts` 委譲)。 */
export function resolvePostRoleCounts(
  post: StreetSurveyPost,
  roleTypeAxis: RoleTypeAxisResolution,
): Record<string, number> {
  if (!roleTypeAxis.axis) return {};
  return resolvePostCategoryCounts(post, {
    codeField: roleTypeAxis.axis.codeField,
    master: roleTypeAxis.master,
    byCategoryCd: roleTypeAxis.byCategoryCd,
  });
}

/** role_type 専用の薄いラッパ (既存呼出側との後方互換、`resolveCategoryDisplay` 委譲)。 */
export function resolvePostRoleDisplay(
  post: StreetSurveyPost,
  roleTypeAxis: RoleTypeAxisResolution,
): { color: string; emoji?: string } | null {
  if (!roleTypeAxis.axis) return null;
  return resolveCategoryDisplay(post, {
    codeField: roleTypeAxis.axis.codeField,
    master: roleTypeAxis.master,
    byCategoryCd: roleTypeAxis.byCategoryCd,
  });
}

export interface Box2dRect {
  /** % (画像コンテナ幅に対する左端位置)。 */
  left: number;
  /** % (画像コンテナ高さに対する上端位置)。 */
  top: number;
  width: number;
  height: number;
}

/**
 * `box2d` ([ymin,xmin,ymax,xmax]、0〜1000正規化、左上原点、docs/25 §6-3) を画像内の
 * %矩形に変換する (docs/25c 「ぼんやりエリア表示」)。既存のピン中心点計算
 * (`(box2d[1]+box2d[3])/2/1000*100` 等) と同じ座標系の前提を矩形全体に拡張したもの。
 */
export function boxToRect(box2d: [number, number, number, number]): Box2dRect {
  const [ymin, xmin, ymax, xmax] = box2d;
  return {
    left: (xmin / 1000) * 100,
    top: (ymin / 1000) * 100,
    width: ((xmax - xmin) / 1000) * 100,
    height: ((ymax - ymin) / 1000) * 100,
  };
}

/** 方向の表示名 (shared-types の定義を使う、docs/98 R-98 低)。 */
const DIRECTION_LABELS = STREET_SURVEY_DIRECTION_LABELS;

/**
 * 検出の方角・距離・根拠の解決は shared-types に移した (2026-09-18)。
 */
export {
  resolvePinLocation,
  resolveReasonKey,
  resolveReasonText,
} from '@citizen-walk/shared-types';

export interface PinDetail {
  title: string;
  subtitle?: string;
  body: string;
}

/**
 * 検出要素の見出し (docs/98 R-98 中)。選んだ軸の値をマスタの行で名前にする → 軸の名称の列 → 値そのもの。
 * `category_name` / `gql_name` / `object_name` のような特定のテンプレートの列名は使わない。
 */
export function resolveClassificationTitle(
  classification: Record<string, unknown>,
  primaryAxis: PrimaryAxisResolution,
): string {
  // 実体は shared-types (画面とエクスポートで同じ見出しにするため、2026-09-18)。
  return resolveClassificationTitleByAxis(classification, {
    codeField: primaryAxis.primaryAxis?.codeField,
    nameField: primaryAxis.primaryAxis?.nameField,
    byCategoryCd: primaryAxis.byCategoryCd,
  });
}

/**
 * classification から表示用タイトルと根拠テキストを mode 非依存で解決する共通ロジック
 * (`resolvePinDetail` / `resolvePinTooltip` 共有)。`category_name` (walker 固有) 決め打ちだと
 * 他モードで undefined になる不具合があったため、primaryAxis 解決済みの row.category を優先し、
 * 根拠テキストも M3 の `reasoningField` から動的に読む (2026-07-14)。
 */
function resolvePinLabelAndReasoning(
  post: StreetSurveyPost,
  primaryAxis: PrimaryAxisResolution,
  rank: number,
  reasoningField?: string,
): { label: string; axisLabel?: string; reasoning: string } | null {
  const classification = post.classifications.find((c) => c.rank === rank);
  if (!classification) return null;
  const record = classification as unknown as Record<string, unknown>;
  const label = resolveClassificationTitle(record, primaryAxis);
  // 選んだ軸の値の名前。見出しと同じ値になりうるが、その場合は表示側で重複を避ける (呼出側が label と比較する)。
  const axisLabel = label !== '(分類名未設定)' ? label : undefined;
  const reasoning = resolveReasonText(post, record, reasoningField);
  return { label, axisLabel, reasoning };
}

/** ピンhover時の吹き出し表示用の構造化データ (docs/25 §4-9)。 */
export function resolvePinDetail(
  post: StreetSurveyPost,
  primaryAxis: PrimaryAxisResolution,
  rank: number,
  reasoningField?: string,
): PinDetail | null {
  const resolved = resolvePinLabelAndReasoning(post, primaryAxis, rank, reasoningField);
  if (!resolved) return null;
  return {
    title: `Rank ${rank}: ${resolved.label}`,
    subtitle:
      resolved.axisLabel && resolved.axisLabel !== resolved.label ? resolved.axisLabel : undefined,
    body: resolved.reasoning,
  };
}

export function resolvePinTooltip(
  post: StreetSurveyPost,
  primaryAxis: PrimaryAxisResolution,
  rank: number,
  roleTypeAxis?: RoleTypeAxisResolution,
  reasoningField?: string,
): string {
  const resolved = resolvePinLabelAndReasoning(post, primaryAxis, rank, reasoningField);
  if (!resolved) return `#${rank}`;
  const emoji = roleTypeAxis ? resolvePinRoleEmoji(post, roleTypeAxis, rank) : undefined;
  const emojiPrefix = emoji ? `${emoji} ` : '';
  return `#${rank} ${emojiPrefix}${resolved.label}: ${resolved.reasoning}`;
}
